<template>
	<Panel title="Retail Summary">
		<div class="numbers-summary">
			<div class="summary-table">
				<div class="header-row seven-cols">
					<span>Description</span>
					<span class="text-right">Monthly Payment</span>
					<span class="text-right">Term</span>
					<span class="text-right">Buy Rate</span>
					<span class="text-right">Sell Rate</span>
					<span class="text-right">Days to 1st Payment</span>
					<span class="text-right">First Payment Date</span>
				</div>
				<div class="data-row seven-cols">
					<span>Loan Information</span>
					<span class="text-right">{{ monthlyPaymentWithProducts }}</span>
					<span class="text-right">{{ loanTerm }}</span>
					<span class="text-right">{{ buyRate }}%</span>
					<span class="text-right">{{ sellRate }}%</span>
					<span class="text-right">{{ daysToFirstPayment ?? '-' }}</span>
					<span class="text-right">{{ firstPaymentDate ?? '-' }}</span>
				</div>
			</div>
		</div>
	</Panel>
</template>

<script setup lang="ts">
	import Panel from '@core/components/Panel.vue';
	import util from '@core/services/util';
	import { computed, inject } from 'vue';
	import FIMenu from '@core/classes/FIMenu';
	import { SaturnGlobalPlugin } from '@core/classes/StaticClasses';
	import CoverageTerm from '@core/classes/CoverageTerm';

	export interface Props {
		data: FIMenu;
	}

	const props = defineProps<Props>();
	const $global = inject<SaturnGlobalPlugin>('$global');

	const buyRate = computed(() => {
		if (!props.data) return '0.00';
		const term = props.data.loanTerms.getAcceptedTerm();
		return term?.buyrate?.toFixed(2) || '0.00';
	});

	const sellRate = computed(() => {
		if (!props.data) return '0.00';
		const term = props.data.loanTerms.getAcceptedTerm();
		return term?.sellrate?.toFixed(2) || '0.00';
	});

	const loanTerm = computed(() => {
		if (!props.data) return '';
		return props.data.loanTerms?.getAcceptedTerm()?.term || '';
	});

	const daysToFirstPayment = computed(() => {
		if (!props.data) return null;
		return props.data.loanTerms?.selectedDaysToFirstPayment ?? null;
	});

	const firstPaymentDate = computed(() => {
		if (!props.data || !daysToFirstPayment.value) return null;
		const today = new Date();
		const paymentDate = new Date(today);
		paymentDate.setDate(today.getDate() + parseInt(daysToFirstPayment.value));
		return paymentDate.toLocaleDateString();
	});

	const monthlyPaymentWithProducts = computed(() => {
		if (!props.data) return '-';
		return getFinalNumbers(true)?.finalPayment?.toLocaleString(undefined, { style: 'currency', currency: 'USD' }) ?? '-';
	});

	const getFinalNumbers = (isFinal: boolean) => {
		const fimenu = props.data;
		const state = $global.selectedStore.storeState;
		const loanTerm = fimenu.loanTerms.getAcceptedTerm();
		const ct = fimenu.coverageTerms.filter(c => c.selected == true)[0];
		const coverageTerm = new CoverageTerm(ct);
		return fimenu.finalNumbers(state, loanTerm, coverageTerm, isFinal);
	};
</script>

<style scoped>
	.numbers-summary {
		padding: 1rem;
		display: flex;
		flex-direction: column;
		gap: 1.5rem;
	}

	.summary-table {
		width: 100%;
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
	}

	.header-row.seven-cols,
	.data-row.seven-cols {
		display: grid;
		grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
		align-items: center;
		font-size: 1rem;
	}

	.header-row {
		font-weight: 700;
		border-bottom: 1px solid var(--border-color, #ccc);
		padding-bottom: 0.25rem;
	}

	.data-row {
		color: var(--text-color);
		min-height: 2.2rem;
	}

	.text-right {
		text-align: right;
	}
</style>
